import type { Customer, CustomerAddressNeighbor, PendingAccessRequest, CustomerAccountSummary } from '@ruta/shared';
import { getCustomer } from './api/routes/customers';
import { queryKeys } from './queryKeys';

export type CustomerDetailCache = {
  customer: Customer;
  customersAtSameAddress: CustomerAddressNeighbor[];
  pendingAccessRequests: PendingAccessRequest[];
  account?: CustomerAccountSummary | null;
};

export function customerDetailQuery(customerId: string) {
  return {
    queryKey: queryKeys.customers.detail(customerId),
    queryFn: () => getCustomer(customerId),
    enabled: !!customerId,
  } as const;
}

export function isCustomerDetailCache(data: unknown): data is CustomerDetailCache {
  return typeof data === 'object' && data !== null && 'customer' in data;
}

export function customerFromDetailCache(data: unknown): Customer | undefined {
  if (isCustomerDetailCache(data)) return data.customer;
  if (typeof data === 'object' && data !== null && 'id' in data) return data as Customer;
  return undefined;
}

export function neighborsFromDetailCache(data: unknown): CustomerAddressNeighbor[] {
  return isCustomerDetailCache(data) ? (data.customersAtSameAddress ?? []) : [];
}

export function pendingAccessRequestsFromDetailCache(data: unknown) {
  return isCustomerDetailCache(data) ? (data.pendingAccessRequests ?? []) : [];
}

export function accountFromDetailCache(data: unknown): CustomerAccountSummary | null {
  return isCustomerDetailCache(data) ? (data.account ?? null) : null;
}

/** Write or patch the shared GET /customers/:id cache without dropping neighbor / access-request slices. */
export function toCustomerDetailCache(
  customer: Customer,
  old?: Partial<CustomerDetailCache> | undefined,
): CustomerDetailCache {
  return {
    customer,
    customersAtSameAddress: old?.customersAtSameAddress ?? [],
    pendingAccessRequests: old?.pendingAccessRequests ?? [],
    ...(old && 'account' in old ? { account: old.account } : {}),
  };
}
